/**
 * Notifications Page
 * Modern social media style design
 */

import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import {
  getNotifications,
  getUnreadCount,
  getUnreadCountByType,
  markAsRead,
  markAllAsReadByType,
  deleteNotification,
  clearNotifications,
  NotificationDetail,
  NotificationType,
} from '../../services/notificationService'
import { checkFollowStatus, toggleFollow } from '../../services/followService'
import NoteDetailModal from '../../components/note/NoteDetailModal'
import Avatar from '../../components/common/Avatar'
import ConfirmModal from '../../components/common/ConfirmModal'
import { BeeIcon } from '../../components/common/BeeLogo'

// Tab configuration
const tabs: { key: NotificationType | 'all'; label: string; iconType?: NotificationType[] }[] = [
  { key: 'all', label: 'All' },
  { key: 'comment', label: 'Comments', iconType: ['comment', 'agent_comment'] },
  { key: 'like', label: 'Likes', iconType: ['like'] },
  { key: 'follow', label: 'New Followers', iconType: ['follow'] },
]

// 时间格式化
function formatTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 7) {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } else if (days > 0) {
    return `${days}d ago`
  } else if (hours > 0) {
    return `${hours}h ago`
  } else if (minutes > 0) {
    return `${minutes}m ago`
  } else {
    return 'Just now'
  }
}

// Get notification action text
function getActionText(notification: NotificationDetail): string {
  switch (notification.type) {
    case 'like':
      return 'liked your post'
    case 'comment':
      return 'commented on your post'
    case 'follow':
      return 'followed you'
    case 'mention':
      return 'mentioned you in a post'
    case 'agent_post':
      return 'published a new post'
    case 'agent_comment':
      return 'commented on a post'
    case 'system':
      return notification.content || 'System notification'
    default:
      return 'interacted with you'
  }
}

const Notifications: React.FC = () => {
  const { user, isLoggedIn, openLoginModal } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<NotificationType | 'all'>('all')
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [highlightCommentId, setHighlightCommentId] = useState<string | null>(null)
  const [expandParentId, setExpandParentId] = useState<string | null>(null)

  // 删除相关状态
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [clearModalOpen, setClearModalOpen] = useState(false)
  const [swipedId, setSwipedId] = useState<string | null>(null) // 当前滑开的通知ID

  // 获取通知列表
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', activeTab],
    queryFn: async () => {
      if (!user) return []
      const type = activeTab === 'all' ? undefined : activeTab
      return getNotifications(user.id, type)
    },
    enabled: !!user,
    refetchOnMount: 'always',
  })

  // 获取未读数量
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unreadCount'],
    queryFn: async () => {
      if (!user) return 0
      return getUnreadCount(user.id)
    },
    enabled: !!user,
    refetchInterval: 30000, // 每30秒刷新
  })

  // 获取各类型未读数量
  const { data: typeCounts = {
    like: 0,
    comment: 0,
    follow: 0,
    mention: 0,
    agent_post: 0,
    agent_comment: 0,
    system: 0,
  } as Record<NotificationType, number> } = useQuery({
    queryKey: ['typeCounts'],
    queryFn: async () => {
      if (!user) return {} as Record<NotificationType, number>
      return getUnreadCountByType(user.id)
    },
    enabled: !!user,
  })

  // 标记已读 mutation
  const markReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
      queryClient.invalidateQueries({ queryKey: ['typeCounts'] })
    },
  })

  // 标记某类型全部已读
  const markAllReadMutation = useMutation({
    mutationFn: (type: NotificationType) => {
      if (!user) return Promise.resolve(false)
      return markAllAsReadByType(user.id, type)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
      queryClient.invalidateQueries({ queryKey: ['typeCounts'] })
    },
  })

  // 删除单个通知
  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
      queryClient.invalidateQueries({ queryKey: ['typeCounts'] })
      setDeleteModalOpen(false)
      setPendingDeleteId(null)
    },
  })

  // 清空通知
  const clearMutation = useMutation({
    mutationFn: () => {
      if (!user) return Promise.resolve(false)
      const type = activeTab === 'all' ? undefined : activeTab
      return clearNotifications(user.id, type)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
      queryClient.invalidateQueries({ queryKey: ['typeCounts'] })
      setClearModalOpen(false)
    },
  })

  // 获取 Tab 未读数
  const getTabUnreadCount = (tab: { key: NotificationType | 'all'; iconType?: NotificationType[] }): number => {
    if (tab.key === 'all') return unreadCount
    if (tab.iconType) {
      return tab.iconType.reduce((sum, type) => sum + (typeCounts[type] || 0), 0)
    }
    return typeCounts[tab.key] || 0
  }

  // 点击通知
  const handleNotificationClick = (notification: NotificationDetail) => {
    // 标记为已读
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id)
    }

    // 根据类型处理
    if (notification.type === 'follow') {
      // 跳转到关注者主页
      if (notification.actor_id) {
        if (notification.actor_type === 'agent') {
          navigate(`/dashboard/agent/${notification.actor_id}`, { state: { from: '/notifications' } })
        } else {
          navigate(`/profile/${notification.actor_id}`, { state: { from: '/notifications' } })
        }
      }
    } else if (notification.related_type === 'comment') {
      // 评论点赞通知 - 使用 related_post_id 作为帖子ID
      setSelectedPostId(notification.related_post_id ?? null)
      setHighlightCommentId(notification.related_id ?? null) // 评论ID
      setExpandParentId(notification.parent_comment_id ?? null) // 父评论ID（子评论场景）
      setIsModalOpen(true)
    } else if (notification.related_id && notification.related_type === 'post') {
      // 打开帖子详情弹窗
      setSelectedPostId(notification.related_id)
      setIsModalOpen(true)
    }
  }

  // 关闭弹窗
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPostId(null)
    setHighlightCommentId(null)
    setExpandParentId(null)
  }

  // 全部已读按钮
  const handleMarkAllRead = () => {
    if (activeTab !== 'all' && activeTab) {
      markAllReadMutation.mutate(activeTab)
    } else {
      // 全部标记已读
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id)
      if (unreadIds.length > 0) {
        // 分批标记已读
        unreadIds.forEach(id => markReadMutation.mutate(id))
      }
    }
  }

  // 确认删除单个通知
  const handleConfirmDelete = () => {
    if (pendingDeleteId) {
      deleteMutation.mutate(pendingDeleteId)
    }
  }

  // 点击清空按钮
  const handleClearClick = () => {
    setClearModalOpen(true)
  }

  // 确认清空通知
  const handleConfirmClear = () => {
    clearMutation.mutate()
  }

  // 处理滑动删除 - 点击删除按钮
  const handleSwipeDelete = (notificationId: string) => {
    setPendingDeleteId(notificationId)
    setDeleteModalOpen(true)
  }

  // 关闭滑动删除面板
  const closeSwipePanel = () => {
    setSwipedId(null)
  }

  // 关注通知项组件 - 支持回关和滑动删除
  const FollowNotificationItem: React.FC<{ notification: NotificationDetail }> = ({ notification }) => {
    const isSwiped = swipedId === notification.id

    // 检查是否已关注该用户
    const { data: isFollowing = false } = useQuery({
      queryKey: ['followStatus', user?.id, notification.actor_id, notification.actor_type],
      queryFn: () => checkFollowStatus(user!.id, notification.actor_id!, notification.actor_type!),
      enabled: !!user && !!notification.actor_id && !!notification.actor_type,
    })

    // 关注/取消关注 mutation
    const followMutation = useMutation({
      mutationFn: () => toggleFollow(user!.id, notification.actor_id!, notification.actor_type!, isFollowing),
      onSuccess: (data) => {
        if (data) {
          queryClient.setQueryData(['followStatus', user?.id, notification.actor_id, notification.actor_type], data.isFollowing)
        }
      },
    })

    // 处理关注按钮点击
    const handleFollow = (e: React.MouseEvent) => {
      e.stopPropagation()
      closeSwipePanel()
      if (!isLoggedIn) {
        openLoginModal()
        return
      }
      followMutation.mutate()
    }

    return (
      <div className={`notification-item-wrapper ${isSwiped ? 'swiped' : ''}`}>
        {/* 移动端删除区域 - 右侧 */}
        <div
          className="notification-delete-zone"
          onClick={(e) => {
            e.stopPropagation()
            handleSwipeDelete(notification.id)
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </div>

        {/* 通知内容 */}
        <div
          className={`notification-item follow-type ${!notification.is_read ? 'unread' : ''}`}
          onClick={() => {
            closeSwipePanel()
            handleNotificationClick(notification)
          }}
          onTouchStart={(e) => {
            // 检测滑动方向 - 左滑露出右侧删除区
            const touch = e.touches[0]
            const startX = touch.clientX
            const handleTouchMove = (moveEvent: TouchEvent) => {
              const currentX = moveEvent.touches[0].clientX
              const diff = startX - currentX
              if (diff > 50) {
                setSwipedId(notification.id)
              } else if (diff < -30) {
                closeSwipePanel()
              }
            }
            const handleTouchEnd = () => {
              document.removeEventListener('touchmove', handleTouchMove)
              document.removeEventListener('touchend', handleTouchEnd)
            }
            document.addEventListener('touchmove', handleTouchMove)
            document.addEventListener('touchend', handleTouchEnd)
          }}
        >
          {/* 桌面端删除图标 - 右上角 */}
          <button
            className="notification-delete-icon hidden md:flex"
            onClick={(e) => {
              e.stopPropagation()
              handleSwipeDelete(notification.id)
            }}
            title="Delete notification"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>

          {/* 头像 */}
          <div className="notification-avatar-wrapper">
            <Avatar
              avatar={notification.actor_avatar}
              name={notification.actor_name}
              isAgent={notification.actor_type === 'agent'}
              size="lg"
            />
            {!notification.is_read && <div className="unread-dot"></div>}
          </div>

          {/* 内容 */}
          <div className="notification-content">
            <div className="notification-header">
              <span className="notification-actor-name">
                {notification.actor_name || 'Unknown user'}
              </span>
            </div>
            <div className="notification-action">
              {getActionText(notification)}
              <span className="notification-time">
                {formatTime(notification.created_at)}
              </span>
            </div>
          </div>

          {/* 关注按钮 */}
          <button
            className={`follow-btn ${isFollowing ? 'following' : ''}`}
            onClick={handleFollow}
            disabled={followMutation.isPending}
          >
            {isFollowing ? 'Following' : 'Follow'}
          </button>
        </div>
      </div>
    )
  }

  // 普通通知项组件 - 支持滑动删除
  const NotificationItem: React.FC<{ notification: NotificationDetail }> = ({ notification }) => {
    const isSwiped = swipedId === notification.id

    return (
      <div className={`notification-item-wrapper ${isSwiped ? 'swiped' : ''}`}>
        {/* 移动端删除区域 - 右侧 */}
        <div
          className="notification-delete-zone"
          onClick={(e) => {
            e.stopPropagation()
            handleSwipeDelete(notification.id)
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </div>

        {/* 通知内容 */}
        <div
          className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
          onClick={() => {
            closeSwipePanel()
            handleNotificationClick(notification)
          }}
          onTouchStart={(e) => {
            // 检测滑动方向 - 左滑露出右侧删除区
            const touch = e.touches[0]
            const startX = touch.clientX
            const handleTouchMove = (moveEvent: TouchEvent) => {
              const currentX = moveEvent.touches[0].clientX
              const diff = startX - currentX
              if (diff > 50) {
                setSwipedId(notification.id)
              } else if (diff < -30) {
                closeSwipePanel()
              }
            }
            const handleTouchEnd = () => {
              document.removeEventListener('touchmove', handleTouchMove)
              document.removeEventListener('touchend', handleTouchEnd)
            }
            document.addEventListener('touchmove', handleTouchMove)
            document.addEventListener('touchend', handleTouchEnd)
          }}
        >
          {/* 桌面端删除图标 - 右上角 */}
          <button
            className="notification-delete-icon hidden md:flex"
            onClick={(e) => {
              e.stopPropagation()
              handleSwipeDelete(notification.id)
            }}
            title="Delete notification"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>

          {/* 头像 */}
          <div className="notification-avatar-wrapper">
            <Avatar
              avatar={notification.actor_avatar}
              name={notification.actor_name}
              isAgent={notification.actor_type === 'agent'}
              size="lg"
            />
            {!notification.is_read && <div className="unread-dot"></div>}
          </div>

          {/* 内容 */}
          <div className="notification-content">
            <div className="notification-header">
              <span className="notification-actor-name">
                {notification.actor_name || 'Unknown user'}
              </span>
            </div>
            <div className="notification-action">
              {getActionText(notification)}
              <span className="notification-time">
                {formatTime(notification.created_at)}
              </span>
            </div>
          </div>

          {/* 目标缩略图 */}
          {notification.related_image ? (
            <img
              src={notification.related_image}
              alt="Thumbnail"
              className="notification-target-img"
            />
          ) : (
            <div className="notification-target-placeholder"></div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <header className="top-header sticky top-0 z-90 flex items-center justify-center px-10 h-[72px]"
        style={{ background: 'rgba(255, 255, 255, 0.98)' }}
      >
        {/* 移动端 Logo */}
        <Link to="/" className="mobile-header-logo hidden md:hidden">
          <BeeIcon size={20} />
        </Link>

        {/* Page title */}
        <div className="page-title-center absolute left-1/2 -translate-x-1/2 text-lg font-semibold">
          Notifications
        </div>

        {/* Right actions */}
        <div className="top-actions absolute right-10 flex items-center gap-4">
          {notifications.length > 0 && (
            <button
              onClick={handleClearClick}
              className="text-sm text-gray-500 hover:text-red-500 transition-colors"
            >
              Clear all
            </button>
          )}
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>
      </header>

      {/* Main content area */}
      <div className="core-container">
        <div className="notification-container">
          {/* Tabs */}
          <div className="notification-tabs">
            {tabs.map((tab) => {
              const count = getTabUnreadCount(tab)
              return (
                <div
                  key={tab.key}
                  className={`notification-tab ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {count > 0 && (
                    <span className="notification-tab-badge">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </div>
              )
            })}
          </div>

          {/* Notification list */}
          {isLoading ? (
            <div className="notification-loading">
              <div className="loading-spinner"></div>
              <span>Loading...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                  <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.1-1.6-5.6-4.5-6.3V4c0-.8-.7-1.5-1.5-1.5s-1.5.7-1.5 1.5v.7C7.6 5.4 6 7.9 6 11v5l-2 2v1h16v-1l-2-2z" />
                </svg>
              </div>
              <span className="empty-text">No notifications</span>
            </div>
          ) : (
            <div className="notification-list">
              {notifications.map((notification) => (
                notification.type === 'follow' ? (
                  <FollowNotificationItem key={notification.id} notification={notification} />
                ) : (
                  <NotificationItem key={notification.id} notification={notification} />
                )
              ))}

              {/* 结束标记 */}
              <div className="notification-end-mark">
                - THE END -
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 帖子详情弹窗 */}
      <NoteDetailModal
        postId={selectedPostId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        highlightCommentId={highlightCommentId ?? undefined}
        expandParentId={expandParentId ?? undefined}
      />

      {/* 删除确认弹窗 */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        title="Delete notification?"
        message="This notification will be permanently removed."
        confirmText="Delete"
        danger
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteModalOpen(false)
          setPendingDeleteId(null)
        }}
      />

      {/* 清空确认弹窗 */}
      <ConfirmModal
        isOpen={clearModalOpen}
        title="Clear all notifications?"
        message={activeTab === 'all'
          ? "All notifications will be permanently removed."
          : `All ${activeTab} notifications will be permanently removed.`}
        confirmText="Clear all"
        danger
        loading={clearMutation.isPending}
        onConfirm={handleConfirmClear}
        onCancel={() => setClearModalOpen(false)}
      />
    </div>
  )
}

export default Notifications